import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  message,
  Modal,
  Card,
  Tag,
  Tooltip,
  Typography,
  Input,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import apiClient from "../utils/apiClient";
import SuperAdminAddAdminModal from "../components/SuperAdminAddAdminModal";

const { Title } = Typography;
const { confirm } = Modal;

const SuperAdminAdmins = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addAdminModalVisible, setAddAdminModalVisible] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [resetLink, setResetLink] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  // ================= FETCH ADMINS =================
  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/superadmin/admins");
      if (response.data?.success) {
        setAdmins(response.data.data || []);
      } else {
        setAdmins([]);
      }
    } catch (error) {
      console.error("Fetch admins error:", error);
      message.error("Failed to fetch admin users");
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  // ================= DELETE =================
  const handleDeleteAdmin = (admin) => {
    confirm({
      title: "Delete Admin User",
      content: `Are you sure you want to delete admin "${admin.name}"? This will also delete their company and all associated data.`,
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await apiClient.delete(`/superadmin/users/${admin._id}`);
          message.success("Admin user deleted successfully");
          fetchAdmins();
        } catch (error) {
          message.error("Failed to delete admin user");
        }
      },
    });
  };

  // ================= GENERATE SETUP LINK =================
  const generateSetupLink = async (user) => {
    if (user.isActive) {
      message.warning("User already onboarded");
      return;
    }

    setGeneratingLink(true);
    try {
      const response = await apiClient.post(
        `/users/${user._id}/reset-password`,
      );

      if (response.data?.resetLink) {
        setResetLink(response.data.resetLink);
        message.success("New setup link generated");
      } else {
        message.error("Failed to generate link");
      }
    } catch (err) {
      console.error(err);
      message.error("Error generating link");
    } finally {
      setGeneratingLink(false);
    }
  };

  // ================= TABLE =================
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
      render: (text) => text || "N/A",
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <Tag color={record.isActive ? "green" : "red"}>
          {record.isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="link"
              onClick={() => {
                setSelectedAdmin(record);
                setResetLink("");
              }}
            >
              👁 View
            </Button>
          </Tooltip>

          <Tooltip title="Delete Admin">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDeleteAdmin(record)}
            >
              Delete
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <Title level={2}>Company Admins Management</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddAdminModalVisible(true)}
          size="large"
        >
          Add New Admin
        </Button>
      </div>

      {/* TABLE */}
      <Card>
        <Table
          columns={columns}
          dataSource={admins}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
        />
      </Card>

      {/* ADD ADMIN MODAL */}
      <SuperAdminAddAdminModal
        visible={addAdminModalVisible}
        onClose={() => setAddAdminModalVisible(false)}
        onSuccess={() => {
          setAddAdminModalVisible(false);
          fetchAdmins();
        }}
      />

      {/* VIEW MODAL */}
      {selectedAdmin && (
        <Modal
          open={true}
          title="Admin Details"
          onCancel={() => {
            setSelectedAdmin(null);
            setResetLink("");
          }}
          footer={null}
        >
          <p>
            <strong>Name:</strong> {selectedAdmin.name}
          </p>
          <p>
            <strong>Email:</strong> {selectedAdmin.email}
          </p>
          <p>
            <strong>Company:</strong> {selectedAdmin.companyName || "N/A"}
          </p>
          <p>
            <strong>Status:</strong>{" "}
            {selectedAdmin.isActive ? "Active" : "Inactive"}
          </p>

          <br />

          {/* ONLY FOR INACTIVE USERS */}
          {!selectedAdmin.isActive && (
            <>
              <Button
                type="primary"
                loading={generatingLink}
                onClick={() => generateSetupLink(selectedAdmin)}
              >
                Generate New Setup Link
              </Button>

              {resetLink && (
                <div style={{ marginTop: 16 }}>
                  <Input.TextArea value={resetLink} readOnly autoSize />
                </div>
              )}
            </>
          )}

          {/* ACTIVE USER */}
          {selectedAdmin.isActive && <Tag color="green">User Onboarded</Tag>}
        </Modal>
      )}
    </div>
  );
};

export default SuperAdminAdmins;
